<template>
  <div :id="`${id}-DatePicker`" class="datepicker-container" :style="containerStyles">
    <div class="datepicker-month">
      <div class="datepicker-month__controls">
        <div class="arrow-button-holder">
          <button
            v-if="showPrev && active"
            type="button"
            tabindex="0"
            class="arrow-button arrow-prev"
            :class="{'button-disable': isPrevMonthDisabled}"
            :disabled="isPrevMonthDisabled"
            @click="changeMonth('prev')"
          >
            <img alt="close lazyload" src="./assets/icons/arrow-left.svg">
          </button>
        </div>
        <div class="month-year-controls">
          <TextButton v-if="active" class="month-year-btn no-translate" :label="monthYearFormatted" @click="toggleYearMonthSelector('month')" />
          <div v-if="active" class="arrow-container" @click.prevent.stop="toggleYearMonthSelector('month')">
            <span :class="selectingYearMonth ? `arrow-up` : `arrow-down`" />
          </div>
        </div>
        <div class="arrow-button-holder">
          <button
            v-if="showNext && active"
            type="button"
            tabindex="0"
            class="arrow-button arrow-next"
            @click="changeMonth('next')"
          >
            <img alt="close" class="lazyload" src="./assets/icons/arrow-right.svg">
          </button>
        </div>
      </div>

      <WeekDays v-if="showWeekdays" :week-days="weekDays" :fontSize="daySize.fontSize" />

      <div class="month-container" :style="monthContainerHeight">
        <TransitionGroup v-if="active" :name="transitionName">
          <div v-for="m in [month]" :key="m.month" class="datepicker-days" :style="gridStyles">
            <div v-for="start in weekStart" :key="start + 'startEmptyDay'" class="datepicker-day" />
            <button
              v-for="day in monthDays"
              :id="`day_${day.format('D')}_${month.month}`"
              :key="day.format('D')"
              v-tippy="tooltipOptions"
              class="datepicker-day"
              :class="{
                selected: isSelected(day) && !isDisabled(day),
                available: isDateAvailable(day),
                checkin: isCheckInDay(day),
                secondary: isDateSecondary(day),
                checkout: isCheckOutDay(day) && !isCheckInDay(day) && !isBeforeStartDate(day),
                pending: isDatePending(day),
                contact: isDateContactUs(day),
                invalid: isInvalid(day),
                disabled: ( isDisabled(day) || isWeekEndDay(day)),
                off: monthInTransition,
                between: isBetween(day) && range,
                first: firstInRange(day) && range,
                last: lastInRange(day) && !!value.end && range
              }"
              :disabled="isDisabled(day) || isWeekEndDay(day)"
              type="button"
              tabindex="0"
              :content="tooltipMessage"
              @click="selectDate(day)"
              @mouseover="checkDate(day)"
            >
              <span class="datepicker-day-holder">
                <!-- <span v-if="isToday(day) && !isSelected(day) && !isDisabled(day) && !isBetween(day)" class="datepicker-today" :style="daySize" /> -->
                <span v-show="!isDisabled(day) || isSelected(day)" class="datepicker-day-effect" :style="daySizeEffect(day)" />
                <span class="datepicker-day-text" :style="daySize">{{ day.format('D') }}</span>
              </span>
            </button>
            <div v-for="end in endEmptyDays" :key="end + 'endEmptyDay'" class="datepicker-day" />
          </div>
        </TransitionGroup>
      </div>
    </div>
    <Transition v-if="active" name="yrm">
      <YearMonthSelector
        v-if="selectingYearMonth"
        :key="selectingYearMonth"
        :locale="locale"
        :mode="selectingYearMonth"
        :month="month"
        :disablePastDates="disablePastDates"
        @input="selectYearMonth"
        @back="selectingYearMonth = null"
        @loadMode="selectingYearMonth = $event"
        @changeYear="changeYear"
        @changeMonth="changeMonth"
      />
    </Transition>
  </div>
</template>

<script>
import moment from 'moment';
import { getWeekDays } from './modules/month';
import WeekDays from './ui/WeekDays';
import YearMonthSelector from './ui/YearMonthSelector';
import TextButton from './ui/TextButton';
import responsive from './mixins/responsive';

export default {
  name: 'DatePicker',
  components: {
    YearMonthSelector,
    WeekDays,
    TextButton,
  },
  mixins: [responsive],
  props: {
    id: { type: String, default: 'main' },
    index: { type: Number, default: 0 },
    value: { type: [String, Object], default: null },

    month: { type: Object, default: null }, // set by parent container
    showPrev: { type: Boolean, default: true }, // set by parent container
    showNext: { type: Boolean, default: true }, // set by parent container
    highlight: { type: Boolean, default: false }, // set by parent container
    transitionDirection: { type: String, default: '' }, // set by parent container

    visible: { type: Boolean, default: false },
    active: { type: Boolean, default: true }, // VIP prop for mobile lazy load optimisation

    range: { type: Boolean, default: false },
    dynamic: { type: Boolean, default: true },
    highlightOnMouseover: { type: Boolean, default: false },
    allowTooltips: { type: Boolean, default: true },
    lazyLoadMonths: { type: Boolean, default: false },
    switchWeekdays: { type: Boolean, default: false },
    firstDayOfWeek: { type: Number, default: 1 },

    // DATE TYPES
    // 1. AVAILABLE - selectable and bookable, white background with border
    // 2. PENDING - non selectable, but can be selected in between dates, background green dashed
    // 3. DISABLED - non selectable, unavailable dates, background grey dashed
    // 4. CONTACT-US - auto generated if date is AVAILABLE + PENDING, selectable but not bookable - background green dashed with border
    // 5. SECONDAY - auto generated if date is non of the above - non selectable, but can be selected in between dates, background white

    availableDates: { type: Array, default: () => ([]) },
    availableDatesWeekly: { type: Array, default: () => ([]) },
    availableCheckInDates: { type: Array, default: () => ([]) },
    availableCheckOutDates: { type: Array, default: () => ([]) },
    pendingDates: { type: Array, default: () => ([]) },
    pendingDatesWeekly: { type: Array, default: () => ([]) },
    disabledDates: { type: Array, default: () => ([]) },
    disabledWeekly: { type: Array, default: () => ([]) },

    disablePastDates: { type: Boolean, default: false },
    noWeekendsDays: { type: Boolean, default: null },
    minDate: { type: String, default: null },
    maxDate: { type: String, default: null },
    minBooking: { type: Number, default: null },
    maxBooking: { type: Number, default: null },

    // style
    fixedHeight: { type: Boolean, default: false },
    inline: { type: Boolean, default: false },
    locale: { type: String, default: 'en' },
    containerWidth: { type: String, default: '284px' },

    monthInTransition: { type: Boolean, default: false },

    messages: {
      type: Object,
      // default: () => ({
      // DAYS: 'nights',
      // MIN_BOOKING: 'Villas have a minimum booking of',
      // MAX_BOOKING: 'Villas have a maximum booking of',
      // NON_BOOKABLE: 'Check in / Check out needs to be on Monday, Saturday, or Sunday',
      // INVALID: 'Some or all of the dates selected are not available. Please select a different date range.',
      // BEFORE_START_DATE: 'This date is before your selected start date. Click to select a new start date.',
      // BEFORE_START_DATE_NON_BOOKABLE: 'This date is before your selected start date.',
      // CONTACT_US: 'This day may be availble but can not be booked online, however you can send us a booking enquiry or contact us by phone to book these dates.',
      // }),
    },

  },
  data() {
    return {
      transitionName: 'slidenext',
      selectingYearMonth: null,
      rangeContainsPendingDates: false,
      rangeSelected: false,
      mousemode: false,
      showTooltip: false,
      tooltipText: '',
      cellSize: 36,
      cellRows: 6,
      gridGap: 2, // 4 minimun
      dateFormat: 'YYYY-MM-DD',
      availableDatesFormatted: [],
      availableCheckInDatesFormatted: [],
      availableCheckOutDatesFormatted: [],
      pendingDatesFormatted: [],
      disabledDatesFormatted: [],
    };
  },
  computed: {
    containerStyles() {
      return {
        width: this.isMobile ? '100%' : this.containerWidth,
      };
    },
    gridStyles() {
      return {
        gridTemplateColumns: 'repeat(7, minmax(20px, 1fr) )',
        gridTemplateRows: `repeat(${this.cellRows}, minmax(${this.cellSize}px, 1fr) )`,
      };
    },
    monthContainerHeight() {
      // FOR ANIMATION TO WORK WE NEED A SET WIDTH AND HEIGHT
      const cells = this.fixedHeight ? 6 : this.cellRows;
      const calculatedHeight = `${Math.round(cells * this.cellSize)}px`; // to make all the same height
      return {
        height: calculatedHeight,
      };
    },
    daySize() {
      return {
        width: `${this.cellSize - this.gridGap}px`,
        height: `${this.cellSize - this.gridGap}px`,
        fontSize: `${Math.round((this.cellSize / 2) - 5)}px`,
      };
    },
    endEmptyDays() {
      const getDays = (this.monthDays.length + this.weekStart) > 35;
      const number = getDays ? 42 : 35;
      return number - this.monthDays.length - this.weekStart;
    },
    showWeekdays() {
      const hide = this.lazyLoadMonths && this.isMobile && this.switchWeekdays;
      return !hide;
    },
    monthDays() {
      return this.month.getMonthDays();
    },
    weekStart() {
      return this.month.getWeekStart();
    },
    monthYearFormatted() {
      return `${this.month.getFormatted()} ${this.month.getYear()}`;
    },
    weekDays() {
      return getWeekDays(this.locale, this.firstDayOfWeek);
    },
    isPrevMonthDisabled() {
      return this.disablePastDates ? (moment(this.month.start).subtract(1, 'months')).isBefore(moment().startOf('month')) : false;
    },
    tooltipOptions() {
      return this.allowTooltips && this.showTooltip ? {
        arrow: true,
        maxWidth: 220,
        theme: 'darkBlue',
        onShow: () => { },
        lazy: true,
      }
        : { onShow: () => false };
    },
    tooltipMessage() {
      return this.tooltipText;
    },
  },
  watch: {
    transitionDirection(val) {
      this.transitionName = `slide${val}`;
    },
    highlight(val) {
      this.mousemode = val;
    },
    value: {
      handler(val) {
        if (this.range && val.start === null) {
          this.mousemode = false;
          this.$emit('mousemode', this.mousemode);
        }
        this.selectingYearMonth = null;
      },
      immediate: true,
      deep: true,
    },
    month() {
      this.cellRows = this.monthDays.length + this.weekStart > 35 ? 6 : 5;
    },
    availableDates() {
      this.availableDatesFormatted = this.availableDates.map((date) => moment(date).format(this.dateFormat));
    },
    availableCheckInDates() {
      this.availableCheckInDatesFormatted = this.availableCheckInDates.map((date) => moment(date).format(this.dateFormat));
    },
    availableCheckOutDates() {
      this.availableCheckOutDatesFormatted = this.availableCheckOutDates.map((date) => moment(date).format(this.dateFormat));
    },
    pendingDates() {
      this.pendingDatesFormatted = this.pendingDates.map((date) => moment(date).format(this.dateFormat));
    },
    disabledDates() {
      this.disabledDatesFormatted = this.disabledDates.map((date) => moment(date).format(this.dateFormat));
    },
  },
  mounted() {
    this.mousemode = this.highlight;
    this.setupDatesData();
  },
  methods: {
    setupDatesData() {
      this.availableDatesFormatted = this.availableDates.map((date) => moment(date).format(this.dateFormat));
      this.pendingDatesFormatted = this.pendingDates.map((date) => moment(date).format(this.dateFormat));
      this.disabledDatesFormatted = this.disabledDates.map((date) => moment(date).format(this.dateFormat));
      this.availableCheckInDatesFormatted = this.availableCheckInDates.map((date) => moment(date).format(this.dateFormat));
      this.availableCheckOutDatesFormatted = this.availableCheckOutDates.map((date) => moment(date).format(this.dateFormat));
    },
    handleScreenResize(screenWidth) {
      gsap.killTweensOf(this.onScreenResize);

      this.cellRows = this.monthDays.length + this.weekStart > 35 ? 6 : 5;

      // can be screen width minus padding for inline to do
      const cw = this.isMobile ? screenWidth : parseInt(this.containerWidth);
      const ratio = this.isMobile ? 9.24 : 1.44;
      const day = document.getElementById(`day_1_${this.month.month}`);
      if (day) {
        const size = ((day.getBoundingClientRect().width).toFixed(2));
        const calculatedSize = ((cw / 7) - ratio).toFixed(2);
        this.cellSize = size > 0 ? size : calculatedSize;
        const fontSize = `${Math.round((this.cellSize / 2) - 3)}px`;
        this.$emit('fontSize', fontSize);
        if (size < 1) {
          gsap.delayedCall(1, this.onScreenResize);
        }
      }
    },
    daySizeEffect(day) {
      return {
        width: this.isBetween(day) ? `${this.cellSize}px` : `${this.cellSize - this.gridGap}px`,
        height: this.isBetween(day) ? `${this.cellSize}px` : `${this.cellSize - this.gridGap}px`,
      };
    },
    isToday(day) {
      return moment(day.format('YYYY-MM-DD')).isSame(moment().format('YYYY-MM-DD'));
    },
    isDisabled(day) {
      return (
        this.isDateDisabled(day) ||
          this.isBeforeMinDate(day) ||
          this.isAfterEndDate(day) ||
          this.isDayDisabledWeekly(day) ||
          (this.isWeekEndDay(day) && this.noWeekendsDays) ||
          (this.disablePastDates ? this.isDatePastDate(day) : false)
      );
    },
    isDatePastDate(day) {
      return day.isBefore(moment().format('YYYY-MM-DD'));
    },
    isDateDisabled(day) {
      return this.disabledDatesFormatted.includes(day.format(this.dateFormat));
    },
    isDayDisabledWeekly(day) {
      return this.disabledWeekly.includes(moment(day).day());
    },
    isBeforeMinDate(day) {
      return day.isBefore(moment(this.minDate, 'YYYY-MM-DD'));
    },
    isAfterEndDate(day) {
      return moment(day).isAfter(this.maxDate);
    },
    isSelected(day) {
      const date = [
        ...(this.value && this.value.start
          ? [moment(this.value.start).format('YYYY-MM-DD')]
          : this.range ? [] : [moment(this.value).format('YYYY-MM-DD')]),
        ...(this.value && this.value.end
          ? [moment(this.value.end).format('YYYY-MM-DD')]
          : this.range ? [] : [moment(this.value).format('YYYY-MM-DD')]),
      ];
      return date.includes(day.format('YYYY-MM-DD'));
    },
    isBetween(day) {
      const range = this.value && this.value.end
        ? moment.range(moment(this.value.start), moment(this.value.end)).contains(day)
        : false;
      return range;
    },
    firstInRange(day) {
      return this.value && this.value.start ? moment(moment(this.value.start).format('YYYY-MM-DD')).isSame(day.format('YYYY-MM-DD')) : false;
    },
    lastInRange(day) {
      return this.value && this.value.end ? moment(moment(this.value.end).format('YYYY-MM-DD')).isSame(day.format('YYYY-MM-DD')) : false;
    },
    isWeekEndDay(day) {
      const dayConst = moment(day).day();
      const d = 7 - this.firstDayOfWeek;
      const weekendsDaysNumbers = this.firstDayOfWeek === 0 || this.firstDayOfWeek > 6 ? [0, 6] : [d - 1, d];
      return this.noWeekendsDays ? weekendsDaysNumbers.includes(dayConst) : false;
    },
    isDateAvailable(day) {
      const dayConst = moment(day).day();
      const allAvailable = this.availableDatesWeekly.length === 0 && this.availableDatesFormatted.length === 0;
    //  console.log(moment(day).format(this.dateFormat) + '' + this.availableDatesWeekly + ' ' + allAvailable);
      if (allAvailable || this.availableDatesWeekly.includes(dayConst)) {
   //     console.log('return 1 true');
        return true;
      }

      if (this.value && (!this.value.start || this.value.end)) {
   //     console.log('return 2 ' + this.isCheckInDay(day));
        return this.isCheckInDay(day);
      }

      if (this.value && this.value.start && !this.value.end) {
   //     console.log('return 3 ' + this.isCheckOutDay(day));
        return this.isCheckOutDay(day);
      }
    //  console.log('return false');

      return false;
     // return allAvailable || this.availableDatesWeekly.includes(dayConst) || this.availableDatesFormatted.includes(day.format(this.dateFormat));
    },
    isCheckInDay(day) {
      return this.availableCheckInDatesFormatted.includes(day.format(this.dateFormat)) || this.conta;
    },
    isCheckOutDay(day) {
      return this.availableCheckOutDatesFormatted.includes(day.format(this.dateFormat));
    },
    // available but not selectable
    isDateSecondary(day) {
      const isOtherDay = this.isDateAvailable(day) || this.isDatePending(day) || this.isDateContactUs(day) || this.isDisabled(day);
      return !isOtherDay;
    },
    // pending availability but not selectable
    isDatePending(day) {
      const dayConst = moment(day).day();
      return !this.isDateAvailable(day) && (this.pendingDatesWeekly.includes(dayConst) || this.pendingDatesFormatted.includes(day.format(this.dateFormat)));
    },
    // pending availability but is selectable to contact for enquiry booking
    isDateContactUs(day) {
      const dayConst = moment(day).day();
      return this.isDateAvailable(day) && (this.pendingDatesWeekly.includes(dayConst) || this.pendingDatesFormatted.includes(day.format(this.dateFormat)));
    },
    isDateSelectable(day) {
      return this.isDateAvailable(day) || this.isDateContactUs(day);
    },
    isBeforeStartDate(day) {
      return day.isBefore(moment(this.value.start));
    },
    isEndDateBetweenDisabledDates(day) {
      const selectedRange = moment.range(moment(this.value.start), moment(day));
      const datesRangeArray = Array.from(selectedRange.by('day'));
      return datesRangeArray.filter((m) => this.isDisabled(m)).length > 0;
    },
    isEndDateBetweenPendingDates(day) {
      const selectedRange = this.range ? moment.range(moment(this.value.start), moment(day)) : moment.range(moment(day), moment(day));
      const datesRangeArray = Array.from(selectedRange.by('day'));
      return datesRangeArray.filter((d) => this.isDatePending(d) || this.isDateContactUs(d)).length > 0;
    },
    isRangeValidMaxBooking(day) {
      const selectedRange = moment.range(moment(this.value.start), moment(day));
      const datesRangeArray = Array.from(selectedRange.by('day'));
      return this.maxBooking > 0 ? datesRangeArray.length <= (this.maxBooking + 1) : true;
    },
    isRangeValidMinBooking(day) {
      const selectedRange = moment.range(moment(this.value.start), moment(day));
      const datesRangeArray = Array.from(selectedRange.by('day'));
      return this.minBooking > 0 ? datesRangeArray.length >= (this.minBooking + 1) : true;
    },
    isInvalid(day) {
      if (this.dynamic && this.mousemode && this.value.start) {
        if (
          day.isBefore(moment(this.value.start))
          || this.isEndDateBetweenDisabledDates(day)
          || !this.isRangeValidMaxBooking(day)
          // || !this.isRangeValidMinBooking(day)
        ) {
          return true;
        }
      }
      return false;
    },

    // Select Date - onClick
    selectDate(day) {
      // console.log('SELECT DAY', day)

      this.showTooltip = false;

      // check if valid select date
      if (!this.isDateSelectable(day)) {
        // console.log('NON SELECTABLE DAY');
        return;
      }

      if (this.range) {
        // Mouse over mode
        if (this.mousemode
            && this.value.start
            && this.value.end
            && !this.isEndDateBetweenDisabledDates(day)
            && this.isRangeValidMaxBooking(day)
            && this.isRangeValidMinBooking(day)
        ) {
          // console.log('click 0 mousemode', this.mousemode);
          this.rangeSelected = true;
          this.mousemode = false;
          this.value.end = day.format('YYYY-MM-DD');
          this.rangeContainsPendingDates = this.isEndDateBetweenPendingDates(day);
          this.$emit('mousemode', this.mousemode);
          this.$emit('input', {
            pending: this.rangeContainsPendingDates, startDate: moment(this.value.start).format('D MMM YYYY'), endDate: moment(this.value.end).format('D MMM YYYY'), ...this.value,
          });
          this.$emit('selection', {
            pending: this.rangeContainsPendingDates, startDate: moment(this.value.start).format('D MMM YYYY'), endDate: moment(this.value.end).format('D MMM YYYY'), ...this.value,
          });
          
          return;
        }
        console.log('mousemode', this.mousemode);
        if (!this.value.start || (this.value.end) || day.isBefore(moment(this.value.start)) || this.isEndDateBetweenDisabledDates(day)) {
          // console.log('select click 1 ---mousemode=', this.mousemode);
          this.rangeSelected = false;
          this.mousemode = true;
          this.value.start = day.format('YYYY-MM-DD');
          this.value.end = null;
          this.$emit('input', { startDate: moment(this.value.start).format('D MMM YYYY'), ...this.value });
          this.$emit('mousemode', this.mousemode);
        } else if (this.isRangeValidMaxBooking(day) && this.isRangeValidMinBooking(day)) {
          // console.log('select click 2');
          this.rangeSelected = true;
          this.mousemode = false;
          this.value.end = day.format('YYYY-MM-DD');
          this.rangeContainsPendingDates = this.isEndDateBetweenPendingDates(day);
          this.$emit('mousemode', this.mousemode);
          this.$emit('selection', { pending: this.rangeContainsPendingDates, startDate: moment(this.value.start).format('D MMM YYYY'), endDate: moment(this.value.end).format('D MMM YYYY'), ...this.value });
          this.$emit('input', {
            pending: this.rangeContainsPendingDates, startDate: moment(this.value.start).format('D MMM YYYY'), endDate: moment(this.value.end).format('D MMM YYYY'), ...this.value,
          });
          
        } else if (this.value.start && !this.value.end && day.isSame(this.value.start)) {
          this.rangeSelected = false;
          this.mousemode = false;
          this.value.start = null;
          this.value.end = null;
          this.$emit('mousemode', this.mousemode);
         
        }
      } else {
        // console.log('click 3');
        // Single date condition - non range
        this.rangeSelected = false;
        // this.rangeContainsPendingDates = this.isEndDateBetweenPendingDates(day);
        this.$emit('input', moment(day).format('YYYY-MM-DD'));
        this.$emit('selection', { pending: this.isEndDateBetweenPendingDates(day), start: moment(day).format('YYYY-MM-DD') });
      }
    },

    // On Mouseover check date
    checkDate(day) {
      this.showTooltip = false;

      if (this.range && this.highlightOnMouseover && this.mousemode) {
        if (
          this.value.start
            && day.isAfter(moment(this.value.start))
            && !this.isEndDateBetweenDisabledDates(day)
            && this.isRangeValidMaxBooking(day)
            && this.isRangeValidMinBooking(day)
            && this.isDateSelectable(day)
        ) {
          // console.log('mouse over 1 select', day)
          this.value.end = day.format('YYYY-MM-DD');
          this.$emit('input', { startDate: moment(this.value.start).format('D MMM YYYY'), ...this.value });
        }
        else if (
          (this.value.start && day.isSameOrBefore(moment(this.value.start)))
            || this.isEndDateBetweenDisabledDates(day)
            || !this.isRangeValidMaxBooking(day)
            || !this.isRangeValidMinBooking(day)
            || !this.isDateSelectable(day)
        ) {
          // console.log('mouse over 2 - DO NOT select', day)
          if (this.allowTooltips) {
            this.checkTooltip(day);
          }
          this.value.end = null;
          this.$emit('input', { startDate: moment(this.value.start).format('D MMM YYYY'), ...this.value });
        }
      }
      else if (this.range && this.mousemode && this.allowTooltips && !this.highlightOnMouseover) {
        this.checkTooltip(day);
      }
    },

    checkTooltip(day) {
      if (this.allowTooltips) {
        if (day.isBefore(moment(this.value.start)) && this.isDateSecondary(day) && this.messages.BEFORE_START_DATE_NON_BOOKABLE) {
          this.tooltipText = this.messages.BEFORE_START_DATE_NON_BOOKABLE;
          this.showTooltip = true;
        }
        else if (day.isBefore(moment(this.value.start)) && this.isDateSecondary(day) && !this.messages.BEFORE_START_DATE_NON_BOOKABLE) {
          this.showTooltip = false;
        }
        else if (day.isBefore(moment(this.value.start)) && this.messages.BEFORE_START_DATE) {
          this.tooltipText = this.messages.BEFORE_START_DATE;
          this.showTooltip = true;
        }
        else if (day.isBefore(moment(this.value.start)) && !this.messages.BEFORE_START_DATE) {
          this.showTooltip = false;
        }
        else if (!this.isRangeValidMinBooking(day) && !day.isSame(moment(this.value.start)) && this.messages.MIN_BOOKING) {
          this.tooltipText = `${this.messages.MIN_BOOKING} ${this.minBooking} ${this.messages.DAYS}${this.messages.MIN_BOOKING_PART2}`;
          this.showTooltip = true;
        }
        else if (!this.isRangeValidMaxBooking(day) && this.messages.MAX_BOOKING) {
          this.tooltipText = `${this.messages.MAX_BOOKING} ${this.maxBooking} ${this.messages.DAYS}`;
          this.showTooltip = true;
        }
        else if (this.isEndDateBetweenDisabledDates(day) && this.messages.INVALID) {
          this.tooltipText = this.messages.INVALID;
          this.showTooltip = true;
        }
        else if ((this.isDatePending(day)) && this.messages.CONTACT_US) {
          this.tooltipText = this.messages.CONTACT_US;
          this.showTooltip = true;
        }
        else if (this.isDateSecondary(day) && this.messages.NON_BOOKABLE) {
          this.tooltipText = this.messages.NON_BOOKABLE;
          this.showTooltip = true;
        }
        else {
          this.showTooltip = false;
        }
      }
    },

    changeMonth(val) {
      this.transitionName = `slide${val}`;
      this.$emit('change-month', val);
    },

    changeYear(payload) {
      const { month, year, dir } = payload;
      const newYear = dir == 'next' ? year + 1 : year - 1;
      const isBefore = newYear === this.month.year
        ? month < this.month.month
        : newYear < this.month.year;

      this.$emit('change-year', { month, year: newYear });
    },

    selectYearMonth(event) {
      let { month, year } = event;

      if (this.isMobile && this.lazyLoadMonths) {
        document.getElementById('monthsContainer').scrollTop = 0;
      }
      else if (!this.isMobile) {
        // Adjust the selected dates based on the date picker index
        month = month < 10 ? `0${month + 1}` : `${month + 1}`;
        const fulldate = moment(`${year}-${month}-01`);
        const adjustedDates = moment(fulldate).subtract(this.index, 'months');
        month = adjustedDates.month();
        year = adjustedDates.year();
      }

      this.selectingYearMonth = null;
      this.$emit('change-year-month', { month, year });
    },

    toggleYearMonthSelector(val) {
      if (this.selectingYearMonth === val) {
        this.selectingYearMonth = null;
      }
      else {
        this.$emit('closeYearMonthSelector');
        this.selectingYearMonth = val;
      }
    },

    closeYearMonthSelector() {
      this.selectingYearMonth = null;
    },

  },
};
</script>

<style lang="scss" scoped>
@import "./assets/datepicker.scss";

$daysize: 100%;

.datepicker-container {
  width: 300px;
  padding: 0 5px;
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  @include dp-mobile-down{
    width: 100% !important;
    max-width: 100% !important;
    padding:0;
  }

  .datepicker-month {
    position: relative;
    width: 100%;

    // top controls
    &__controls {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 60px;

      @include dp-mobile-down(){
        height: 40px;
        margin: 0 5px 7px 5px;
      }

      .arrow-button-holder{
        height: 100%;
        min-width: 30px;
        padding: 0;
        margin: 0;
      }

      //arrow buttons - next/previous month
      .arrow-button {
        background: transparent;
        cursor: pointer;
        border: none;
        outline: none;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        width: 100%;
        height: 100%;
        padding: 0;
        margin: 0;

        svg {
          height: 30px;
          width: 30px;
          fill: $darkBlack;
        }
        svg:hover{
          fill: $darkBlue;
        }
        &.arrow-prev {
          //transform: translateY(0%) rotate(180deg);
          justify-content: flex-end;
        }
        &.arrow-next {
           //justify-content: flex-start;
        }
        &.button-disable{
          opacity: 0.3;
          fill: $darkBlue;
          cursor: auto;
          pointer-events: none;
        }
      }

      //MONTH AND YEAR BUTTON CONTROLS
      .month-year-controls {
        position: relative;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
      }
      .month-year-btn {
        //text-transform: capitalize;
        &:first-child{
          margin-right: 5px;
        }
      }
      .arrow-container{
        margin: 0 10px;
        cursor: pointer;
        .arrow-down{
          @include triangle(6px, $darkBlack, down);
        }
        .arrow-up{
          @include triangle(6px, $darkBlack, up);
        }
      }
    }
  }

  .month-container {
    position: relative;
    overflow: hidden;
    @include dp-mobile-down{
      margin:0 auto;
    }

  }

  //-----days------//
  .datepicker-days {
    display: grid;
    justify-items: stretch;

    //Main holder for DAY
    .datepicker-day {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-grow: 1;
      border: none;
      background: transparent;
      outline: none;
      padding: 0;
      //border:1px solid grey !important;

      //span wrapper for all spans inside button
      .datepicker-day-holder{
        background-color: $stoneWhite;
      //   // width:100%;
      //   // height: 100%;
      }

      &-effect{
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
        width: $daysize;
        height: $daysize;
        -webkit-transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;
        transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;

         @include dp-mobile-down{
          transition: all 100ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;
         }
      }

      //span animation below text
      .datepicker-day-effect {
        margin: auto;
        /*opacity: 0.6;*/
        background: $darkCement;
        transform: scale(0);
      }

      //span below text
      .datepicker-today{
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        margin: auto;
        width: $daysize;
        height: $daysize;
        background-color: rgba(91, 199, 204, 0.3);
      }

      //span text top level
      .datepicker-day-text {
        position: relative;
        display: flex;
        flex: 1;
        justify-content: center;
        align-items: center;
        width: $daysize;
        height: $daysize;
        font-family: $font-family-grotesk;
        font-size: 14px;
        font-weight: 400;
        color: $darkCement;
        letter-spacing: 0.01em;
       @include dp-mobile-down{
          font-size: 22px;
          font-weight: 400;
        }

        @include dp-mobile-down-sm{
          font-size: 16px;
        }
      }

      @mixin day-hover {
        &:hover:not(.disabled):not(.between) {
          .datepicker-day-text {
            color: $stoneWhite !important;
          }
          .datepicker-day-effect {
            transform: scale(1);
            /*opacity: 0.6;*/
            background: $stoneDark;
          }
        }
      }

      &.off {
        cursor: none !important;
        pointer-events: none;
      }

      &.available:not(.disabled) {
        cursor: pointer;
        @include day-hover();
      }
      &.available {
        .datepicker-day-text {
          border: 1px solid $darkCement;
          color: $darkBlack;
        }
      }

      &.checkin {
        .datepicker-day-text {
          border: 1px solid $darkCement;
          color: $darkBlack;
        }
      }

      &.checkout {
        .datepicker-day-text {
          border: 1px solid $yellowStone !important;
          color: $darkBlack !important;
        }
      }

      &.secondary {
        cursor: auto;
        border: none !important;
        .datepicker-day-holder {
          background-color: $stoneWhite;
        }
        .datepicker-day-text {
          border: none;
        }
      }

      &.pending {
        cursor: auto;
        border: none !important;
        .datepicker-day-text {
          border: none;
          background-color: $stoneWhite;
          background: repeating-linear-gradient(
              45deg,
              rgba(5, 8, 23, 0.06),
              rgba(5, 8, 23, 0.06) 2px,
              rgba(255, 255, 255, 0.3) 2px,
              rgba(255, 255, 255, 0.3) 4px
          );
        }
      }

      &.contact:not(.disabled) {
        cursor: pointer;
        pointer-events: auto;
        @include day-hover();
      }

      &.contact {
        .datepicker-day-text {
          border: 1px solid $darkCement;
          background-color: $stoneWhite;
          background: repeating-linear-gradient(
              45deg,
              rgba(5, 8, 23, 0.06),
              rgba(5, 8, 23, 0.06) 2px,
              rgba(255, 255, 255, 0.3) 2px,
              rgba(255, 255, 255, 0.3) 4px
          );
        }
      }

      //for all types
      &.between {
        cursor: pointer;

        .datepicker-day-text {
          color: $darkBlack;
          border: none;
          background: none;
        }
        .datepicker-day-effect {
          transform: scale(1);
          /*opacity: 0.5;*/
          border-radius: 0;
          width: 100%;
          background: $stoneLight;
        }
        // &.first .datepicker-day-effect {
        //   // border-top-left-radius: 4px;
        //   // border-bottom-left-radius: 4px;
        // }
        // &.last .datepicker-day-effect {
        //   // border-top-right-radius: 4px;
        //   // border-bottom-right-radius: 4px;
        // }
      }
      &.selected {
        .datepicker-day-text {
          background-color: $darkBlack !important;
          color: $stoneWhite !important;
          font-weight: 400;
        }
        .datepicker-day-effect {
          transform: scale(1);
          /*opacity: 1;*/
        }
      }

      &.invalid:not(.selected):not(.between) {
        cursor: default !important;
        pointer-events: none;

        .datepicker-day-text {
          background: none;
          color: $stoneDark;
          border-color: $yellowStone;
          /*opacity: 0.8;*/
        }
      }

      &.disabled {
        cursor: default !important;
        pointer-events: none !important;

        .datepicker-day-text {
          color: $stoneDark;
          border: none !important;
          background-color: $limeStone !important;
          background: repeating-linear-gradient(
              45deg,
              rgba(5, 8, 23, 0.1),
              rgba(5, 8, 23, 0.1) 2px,
              rgba(255, 255, 255, 0.3) 2px,
              rgba(255, 255, 255, 0.3) 4px
          ) !important;
        }
        &.selected {
          color: $stoneWhite;
        }
        .datepicker-day-effect {
          transform: scale(0);
          /*opacity: 0;*/
        }
      }

    }
  }
}

</style>
